import { ExpandedCancelIcon, ExpandedSaveIcon } from '@assets';
import {
  Box,
  ConfirmationDialogBox,
  Grid,
  Stack,
  Typography,
} from '@atgfr8/atg-ui-tailwind';
import { Button, Notification, ToastType } from '@components';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';
import { FeatureFlags } from '@constants/featureFlags';
import { createForm } from '@felte/solid';
import { useParams } from '@solidjs/router';
import { customerDetailsStore } from '@store/customers/customerDetails';
import {
  customerLTLSettings,
  getCustomerLTLSettingsLegacy,
  getLtlCustomerSetting,
  LTLBlockedCarrier,
  LTLCostPlus,
  LTLCostPlusWithOverrides,
  ltlCustomerSetting,
  saveCarrierSelection,
  saveCustomerLTLSettingsLegacy,
  saveLtlCustomerSetting,
  setCustomerLTLSettingsLegacy,
} from '@store/customers/customerDetails/LTL';
import {
  checkIfDialogBoxIsOpen,
  closeDialogBox,
  openDialogBox,
} from '@store/dialogBox';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { loadFeatureFlagGetCustomerFeatures } from '@store/orders/actions/orderCustomerActions';
import { featureFlagGetCustomerFeatures } from '@store/orders/store';
import { CarrierInfo } from '@typeDefinitions/carrierTypes';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { handleToast, isLTLAdmin, printError } from '@utils/utils';
import { CANCEL_LTL_CHANGES_DIALOG_ID } from '@views/order/constants';
import { isEmpty, size } from 'lodash';
import { createEffect, createSignal, For, onMount, Show } from 'solid-js';

import {
  BlockedCarriers,
  CarrierSelection,
  CostPlusMargins,
  CustomerSettingAndAlert,
  CustomerSpecificPricing,
  ExternalSetup,
} from './components';
import AlternateCustomer from './components/AlternateCustomer';
import GapLogic, { getGapCompressionError } from './components/GapLogic';
import { ltlComponent as classes } from './components/classes';

export type InitialLTLFormValues = {
  ltlCostPlus: LTLCostPlus;
  ltlCostPlusOverrides: LTLCostPlusWithOverrides[];
  ltlBlockedCarriers: LTLBlockedCarrier[];
  quoteExpirationDays: number | null;
  carrierSelectionStrategyType: number | null;
  carrierIds: number[];
  enableLtlInCustomerPortal: boolean;
  enableCustomerPortalAutoDispatch: boolean;
  enableCustomerSpecificPricing: boolean;
  enablePrePro: boolean;
  enableAgentQuotesInCustomerPortal: boolean;
  enableSuggestedClass: boolean;
  enableMixedCommodities: boolean;
  emailAgentIfShipmentNotBuilt: boolean;
  emailAgentIfNoQuoteResult: boolean;
  disableBolInCustomerPortal: boolean;
  enableLtlGapLogic: boolean;
  gapCompressionPercentage: number | null;
  insuranceTermsAccepted: boolean;
};

const Ltl = () => {
  const [enableLTLWarnings, setEnableLTLWarnings] = createSignal<Array<string>>(
    [],
  );
  const params = useParams();

  const [showCustomerSpecificPricing, setShowCustomerSpecificPricing] =
    createSignal(false);
  const isGapLogicEnabled = isFeatureFlagEnabled(FeatureFlags.LtlGapLogic);
  const [isSettingsDirty, setIsSettingsDirty] = createSignal(false);
  const [isCarriersDirty, setIsCarriersDirty] = createSignal(false);
  const [legacyChangeSources, setLegacyChangeSources] = createSignal<
    ChangeTrackingSource[]
  >([]);
  const [customerSettingChangeSources, setCustomerSettingChangeSources] =
    createSignal<ChangeTrackingSource[]>([]);

  const addLegacyChangeSource = (source: ChangeTrackingSource) => {
    setLegacyChangeSources((current) =>
      current.includes(source) ? current : [...current, source],
    );
  };

  const addCustomerSettingChangeSource = (source: ChangeTrackingSource) => {
    setCustomerSettingChangeSources((current) =>
      current.includes(source) ? current : [...current, source],
    );
  };

  const clearLtlChangeSources = () => {
    setLegacyChangeSources([]);
    setCustomerSettingChangeSources([]);
  };

  const { form, setFields, data, isDirty, setIsDirty } =
    createForm<InitialLTLFormValues>({
      initialValues: {
        ltlCostPlus: {
          type: 'Flat',
          amount: null,
          minimum: null,
          maximum: null,
        },
        ltlCostPlusOverrides: [],
        ltlBlockedCarriers: [],
        quoteExpirationDays: null,
        carrierSelectionStrategyType: null,
        carrierIds: [],
        enableLtlInCustomerPortal: false,
        enableCustomerPortalAutoDispatch: false,
        enableCustomerSpecificPricing: false,
        enablePrePro: false,
        enableAgentQuotesInCustomerPortal: false,
        enableSuggestedClass: false,
        enableMixedCommodities: false,
        emailAgentIfShipmentNotBuilt: false,
        emailAgentIfNoQuoteResult: false,
        disableBolInCustomerPortal: false,
        enableLtlGapLogic: false,
        gapCompressionPercentage: null,
        insuranceTermsAccepted: false,
      },
      onSubmit: async (value) => {
        const gapCompressionError = isGapLogicEnabled()
          ? getGapCompressionError(value)
          : null;
        if (gapCompressionError) {
          handleToast(ToastType.Error, gapCompressionError);
          return;
        }
        const customerId = Number(params.id);
        const legacyPayload = {
          customerId,
          ltlCostPlus: value.ltlCostPlus,
          ltlCostPlusOverrides: value.ltlCostPlusOverrides,
          ltlBlockedCarriers: value.ltlBlockedCarriers,
        };
        const results: unknown[] = [];
        results.push(
          await saveCustomerLTLSettingsLegacy(
            legacyPayload,
            legacyChangeSources(),
          ),
        );
        if (isSettingsDirty()) {
          const settingPayload = {
            customerId,
            quoteExpirationDays: value.quoteExpirationDays,
            carrierSelectionStrategyType: value.carrierSelectionStrategyType,
            enableLtlInCustomerPortal: value.enableLtlInCustomerPortal,
            enableCustomerPortalAutoDispatch:
              value.enableCustomerPortalAutoDispatch,
            enableCustomerSpecificPricing: value.enableCustomerSpecificPricing,
            enablePrePro: value.enablePrePro,
            enableAgentQuotesInCustomerPortal:
              value.enableAgentQuotesInCustomerPortal,
            enableSuggestedClass: value.enableSuggestedClass,
            enableMixedCommodities: value.enableMixedCommodities,
            emailAgentIfShipmentNotBuilt: value.emailAgentIfShipmentNotBuilt,
            emailAgentIfNoQuoteResult: value.emailAgentIfNoQuoteResult,
            disableBolInCustomerPortal: value.disableBolInCustomerPortal,
            enableLtlGapLogic: value.enableLtlGapLogic,
            gapCompressionPercentage: value.gapCompressionPercentage,
            // Admin-only: non-admins send null so the API keeps the stored value.
            insuranceTermsAccepted: isLTLAdmin()
              ? value.insuranceTermsAccepted
              : null,
            carriers: ltlCustomerSetting.data.carriers,
          };
          results.push(
            await saveLtlCustomerSetting(
              settingPayload,
              customerSettingChangeSources(),
            ),
          );
        }
        if (isCarriersDirty()) {
          results.push(
            await saveCarrierSelection(
              customerId,
              value.carrierSelectionStrategyType,
              value.carrierIds,
              [CHANGE_TRACKING_SOURCES.customerDetails.ltlCarrierSelectionCard],
            ),
          );
        }
        if (results.every(Boolean)) {
          setIsSettingsDirty(false);
          setIsCarriersDirty(false);
          clearLtlChangeSources();
          setFieldData();
          size(enableLTLWarnings()) > 0 && setEnableLTLWarnings([]);
        }
      },
    });

  const isValidCarrierChoice = (carrier: CarrierInfo) => {
    const isCarrierLTLBlocked = data().ltlBlockedCarriers.some(
      (v) => v.carrierId === carrier.id,
    );
    const carrierLTLOverrideData = data().ltlCostPlusOverrides.flatMap((v) =>
      v.ltlCostPlusOverrideCarriers.filter((c) => c.carrierId === carrier.id),
    );
    if (isCarrierLTLBlocked) {
      handleToast(
        ToastType.Error,
        `${carrier.name} is set as a blocked carrier.`,
      );
      return true;
    } else if (size(carrierLTLOverrideData) > 0) {
      handleToast(
        ToastType.Error,
        `${carrier.name} is set as a override carrier.`,
      );
      return true;
    }
    return false;
  };

  const setFieldData = () => {
    if (customerLTLSettings.settings.customerId) {
      setFields('ltlCostPlus', {
        ...customerLTLSettings.settings.ltlCostPlus,
        type: customerLTLSettings.settings.ltlCostPlus.type || 'Flat',
      });
      setFields(
        'ltlCostPlusOverrides',
        customerLTLSettings.settings.ltlCostPlusOverrides,
      );
      setFields(
        'ltlBlockedCarriers',
        customerLTLSettings.settings.ltlBlockedCarriers,
      );
    }
    if (ltlCustomerSetting.data.customerId) {
      setFields(
        'enableLtlInCustomerPortal',
        Boolean(ltlCustomerSetting.data.enableLtlInCustomerPortal),
      );
      setFields(
        'enableCustomerPortalAutoDispatch',
        Boolean(ltlCustomerSetting.data.enableCustomerPortalAutoDispatch),
      );
      setFields(
        'enableCustomerSpecificPricing',
        Boolean(ltlCustomerSetting.data.enableCustomerSpecificPricing),
      );
      setFields('enablePrePro', Boolean(ltlCustomerSetting.data.enablePrePro));
      setFields(
        'enableAgentQuotesInCustomerPortal',
        Boolean(ltlCustomerSetting.data.enableAgentQuotesInCustomerPortal),
      );
      setFields(
        'enableSuggestedClass',
        Boolean(ltlCustomerSetting.data.enableSuggestedClass),
      );
      setFields(
        'enableMixedCommodities',
        Boolean(ltlCustomerSetting.data.enableMixedCommodities),
      );
      setFields(
        'emailAgentIfShipmentNotBuilt',
        Boolean(ltlCustomerSetting.data.emailAgentIfShipmentNotBuilt),
      );
      setFields(
        'emailAgentIfNoQuoteResult',
        Boolean(ltlCustomerSetting.data.emailAgentIfNoQuoteResult),
      );
      setFields(
        'disableBolInCustomerPortal',
        Boolean(ltlCustomerSetting.data.disableBolInCustomerPortal),
      );
      setFields(
        'enableLtlGapLogic',
        Boolean(ltlCustomerSetting.data.enableLtlGapLogic),
      );
      setFields(
        'gapCompressionPercentage',
        ltlCustomerSetting.data.gapCompressionPercentage,
      );
      setFields(
        'insuranceTermsAccepted',
        Boolean(ltlCustomerSetting.data.insuranceTermsAccepted),
      );
    }
  };

  createEffect(() => {
    if (ltlCustomerSetting.data.customerId) {
      setFields(
        'quoteExpirationDays',
        ltlCustomerSetting.data.quoteExpirationDays,
      );
      setFields(
        'carrierSelectionStrategyType',
        ltlCustomerSetting.data.carrierSelectionStrategyType,
      );
      setFields(
        'carrierIds',
        ltlCustomerSetting.data.carriers.map((c) => c.carrierId),
      );
      setFields(
        'enableLtlInCustomerPortal',
        Boolean(ltlCustomerSetting.data.enableLtlInCustomerPortal),
      );
      setFields(
        'enableCustomerPortalAutoDispatch',
        Boolean(ltlCustomerSetting.data.enableCustomerPortalAutoDispatch),
      );
      setFields(
        'enableCustomerSpecificPricing',
        Boolean(ltlCustomerSetting.data.enableCustomerSpecificPricing),
      );
      setFields('enablePrePro', Boolean(ltlCustomerSetting.data.enablePrePro));
      setFields(
        'enableAgentQuotesInCustomerPortal',
        Boolean(ltlCustomerSetting.data.enableAgentQuotesInCustomerPortal),
      );
      setFields(
        'enableSuggestedClass',
        Boolean(ltlCustomerSetting.data.enableSuggestedClass),
      );
      setFields(
        'enableMixedCommodities',
        Boolean(ltlCustomerSetting.data.enableMixedCommodities),
      );
      setFields(
        'emailAgentIfShipmentNotBuilt',
        Boolean(ltlCustomerSetting.data.emailAgentIfShipmentNotBuilt),
      );
      setFields(
        'emailAgentIfNoQuoteResult',
        Boolean(ltlCustomerSetting.data.emailAgentIfNoQuoteResult),
      );
      setFields(
        'disableBolInCustomerPortal',
        Boolean(ltlCustomerSetting.data.disableBolInCustomerPortal),
      );
      setFields(
        'enableLtlGapLogic',
        Boolean(ltlCustomerSetting.data.enableLtlGapLogic),
      );
      setFields(
        'gapCompressionPercentage',
        ltlCustomerSetting.data.gapCompressionPercentage,
      );
      setFields(
        'insuranceTermsAccepted',
        Boolean(ltlCustomerSetting.data.insuranceTermsAccepted),
      );
    }
  });

  const onRequestStrategyToggle = (next: number) => {
    setFields('carrierSelectionStrategyType', next);
    setIsDirty(true);
    setIsSettingsDirty(true);
    addCustomerSettingChangeSource(
      CHANGE_TRACKING_SOURCES.customerDetails.ltlCarrierSelectionCard,
    );
  };

  const loadCustomerData = (id: string) => {
    getLtlCustomerSetting(Number(id)).catch(printError);
    loadFeatureFlagGetCustomerFeatures(Number(id)).catch(printError);
  };

  onMount(() => {
    setFieldData();
    setShowCustomerSpecificPricing(
      isFeatureFlagEnabled(FeatureFlags.CarrierLtlSetup)(),
    );
    loadCustomerData(params.id!);
  });

  let previousId = params.id;
  createEffect(async () => {
    if (previousId !== params.id) {
      previousId = params.id;
      setIsDirty(false);
      setIsSettingsDirty(false);
      setIsCarriersDirty(false);
      clearLtlChangeSources();
      await getCustomerLTLSettingsLegacy(params.id as string);
      setFieldData();
      setShowCustomerSpecificPricing(
        isFeatureFlagEnabled(FeatureFlags.CarrierLtlSetup)(),
      );
      loadCustomerData(params.id!);
    }
  });

  return (
    <form ref={form}>
      <Box>
        <Stack class="w-full">
          <For each={enableLTLWarnings()}>
            {(item: string) => <Notification type="error" text={item} />}
          </For>
          <Show when={!isEmpty(customerLTLSettings.errorMessage)}>
            <Notification
              sxProps="mb-5!"
              type="error"
              text={
                <For each={customerLTLSettings.errorMessage.split(',')}>
                  {(item) => (
                    <Typography component="p" variant="body1">
                      {`${item}`}
                    </Typography>
                  )}
                </For>
              }
            />
          </Show>
          <Box class="flex px-2 items-center w-full mb-[8px]">
            <Stack>
              <Box class="text-[24px] font-500">Manage LTL Settings</Box>
              <Box class="text-[14px]">
                Use these settings to manage your customer's Customer Portal
                experience as well as your margins.
              </Box>
            </Stack>
            <Box class="ml-auto">
              <Button
                size="sm"
                startIcon={
                  <img
                    src={ExpandedSaveIcon}
                    alt={ExpandedSaveIcon}
                    class={classes.buttonStyle}
                  />
                }
                type="submit"
                variant="contained"
                label="SAVE LTL SETTINGS"
                isLoading={
                  customerLTLSettings.isLoading || ltlCustomerSetting.isLoading
                }
                disabled={
                  customerLTLSettings.isLoading || ltlCustomerSetting.isLoading
                }
                dataTestId={formatDataTestId('SAVE LTL SETTINGS', 'button')}
              />
              <Button
                size="sm"
                startIcon={
                  <img
                    src={ExpandedCancelIcon}
                    alt={ExpandedCancelIcon}
                    class={classes.buttonStyle}
                  />
                }
                variant="contained"
                label="Cancel"
                onClick={() =>
                  isDirty() && openDialogBox(CANCEL_LTL_CHANGES_DIALOG_ID)
                }
                class={classes.expandButton}
                disabled={customerLTLSettings.isLoading}
                dataTestId={formatDataTestId('Cancel', 'button')}
              />
            </Box>
          </Box>
          <CostPlusMargins
            setFields={setFields}
            data={data}
            isValidCarrierChoice={isValidCarrierChoice}
            setIsDirty={setIsDirty}
            onSectionChange={() =>
              addLegacyChangeSource(
                CHANGE_TRACKING_SOURCES.customerDetails.ltlCostPlusMargins,
              )
            }
          />
          <Show when={isGapLogicEnabled()}>
            <GapLogic
              setFields={setFields}
              data={data}
              setIsDirty={setIsDirty}
              setIsSettingsDirty={setIsSettingsDirty}
              onSectionChange={() =>
                addCustomerSettingChangeSource(
                  CHANGE_TRACKING_SOURCES.customerDetails
                    .ltlCustomerSettingsAndEmailAlerts,
                )
              }
            />
          </Show>
          <Grid container spacing={2} sx={{ width: '100%' }}>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                <CustomerSettingAndAlert
                  setFields={setFields}
                  data={data}
                  setEnableLTLWarnings={setEnableLTLWarnings}
                  setIsDirty={setIsDirty}
                  setIsSettingsDirty={setIsSettingsDirty}
                  onSectionChange={() =>
                    addCustomerSettingChangeSource(
                      CHANGE_TRACKING_SOURCES.customerDetails
                        .ltlCustomerSettingsAndEmailAlerts,
                    )
                  }
                  showQuoteExpiration={
                    featureFlagGetCustomerFeatures.featureFlagCustomer
                      .LtlQuoteExpiration
                  }
                />
                <ExternalSetup customerId={customerDetailsStore.customer.id} />
                <Show when={showCustomerSpecificPricing()}>
                  <AlternateCustomer
                    customerId={customerLTLSettings.settings.customerId}
                  />
                </Show>
              </Stack>
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Stack spacing={2}>
                <Show
                  when={
                    !featureFlagGetCustomerFeatures.featureFlagCustomer
                      .LtlCarrierBlockInclude
                  }
                >
                  <BlockedCarriers
                    setFields={setFields}
                    data={data}
                    isValidCarrierChoice={isValidCarrierChoice}
                    setIsDirty={setIsDirty}
                    onSectionChange={() =>
                      addLegacyChangeSource(
                        CHANGE_TRACKING_SOURCES.customerDetails
                          .ltlBlockedCarriers,
                      )
                    }
                  />
                </Show>
                <Show
                  when={
                    featureFlagGetCustomerFeatures.featureFlagCustomer
                      .LtlCarrierBlockInclude
                  }
                >
                  <CarrierSelection
                    setFields={setFields}
                    data={data}
                    setIsDirty={setIsDirty}
                    setIsCarriersDirty={setIsCarriersDirty}
                    onRequestStrategyToggle={onRequestStrategyToggle}
                    onSectionChange={() =>
                      addCustomerSettingChangeSource(
                        CHANGE_TRACKING_SOURCES.customerDetails
                          .ltlCarrierSelectionCard,
                      )
                    }
                  />
                </Show>
                <Show when={showCustomerSpecificPricing()}>
                  <CustomerSpecificPricing
                    customerId={customerLTLSettings.settings.customerId}
                  />
                </Show>
              </Stack>
            </Grid>
          </Grid>
        </Stack>
      </Box>
      <ConfirmationDialogBox
        isDialogOpen={checkIfDialogBoxIsOpen(CANCEL_LTL_CHANGES_DIALOG_ID)}
        dataTestId={CANCEL_LTL_CHANGES_DIALOG_ID}
        id={CANCEL_LTL_CHANGES_DIALOG_ID}
        title={
          // eslint-disable-next-line quotes
          "Are you sure you want to reset all LTL Settings changes you've made?"
        }
        onSubmit={() => {
          setCustomerLTLSettingsLegacy('errorMessage', '');
          clearLtlChangeSources();
          setFieldData();
        }}
        onSubmitText="Ok"
        closeDialogBox={(_reason) =>
          closeDialogBox(CANCEL_LTL_CHANGES_DIALOG_ID)
        }
      />
    </form>
  );
};

export default Ltl;
